// Tips and guides: collection cards, then a collection's articles as an
// accordion with numbered steps. Content is TIP_COLLECTIONS, unchanged. The
// open collection is in the URL (?c=) so Back works and links can be shared.
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Button, EmptyState, Input, PageHeader, cx } from '@app/components/ui';
import { TIP_COLLECTIONS, type TipCollection } from '../tips/tipsContent';

const matches = (q: string, c: TipCollection) =>
  c.articles.filter(
    (a) =>
      a.title.toLowerCase().includes(q) ||
      a.summary.toLowerCase().includes(q) ||
      a.steps.some((s) => s.title.toLowerCase().includes(q) || s.detail.toLowerCase().includes(q)),
  );

export const Tips: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const collection = TIP_COLLECTIONS.find((c) => c.id === params.get('c')) ?? null;

  const cards = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TIP_COLLECTIONS.map((c) => ({ c, articles: q ? matches(q, c) : c.articles })).filter((x) => x.articles.length > 0);
  }, [query]);

  if (collection) {
    return (
      <div className="max-w-[780px]">
        <Button variant="link" className="mb-3.5" onClick={() => setParams({})}>
          ← All tips
        </Button>
        <h1 className="wp-h1 !text-[clamp(28px,4.5vw,36px)] mb-2">{collection.title}</h1>
        <p className="text-[14.5px] text-t3 mb-[22px] leading-[1.6]">{collection.description}</p>
        <div className="flex flex-col gap-2.5">
          {collection.articles.map((a) => {
            const open = openId === a.id;
            const panel = `tip-${a.id}`;
            return (
              <div key={a.id} className="rounded-[22px] border border-line bg-card">
                <h2>
                  <button
                    type="button"
                    aria-expanded={open}
                    aria-controls={panel}
                    onClick={() => setOpenId(open ? null : a.id)}
                    className="flex justify-between gap-3 w-full text-left px-5 py-[18px]"
                  >
                    <span>
                      <span className="block text-[15.5px] font-semibold">{a.title}</span>
                      <span className="block text-[13.5px] text-t4 mt-[3px] leading-normal">{a.summary}</span>
                    </span>
                    <span className="text-t4 text-[20px] leading-none" aria-hidden="true">
                      {open ? '−' : '+'}
                    </span>
                  </button>
                </h2>
                {open && (
                  <div id={panel} className="px-5 pb-5 wp-fade">
                    <ol>
                      {a.steps.map((s, i) => (
                        <li key={s.title} className="flex gap-3.5 py-3 border-t border-line">
                          <span className="w-[26px] h-[26px] rounded-full bg-acc-bg text-acc font-mono text-[12px] font-bold flex items-center justify-center shrink-0" aria-hidden="true">
                            {i + 1}
                          </span>
                          <div>
                            <div className="text-[14px] font-semibold">{s.title}</div>
                            <div className="text-[13.5px] text-t3 mt-[3px] leading-[1.55] wp-pretty">{s.detail}</div>
                          </div>
                        </li>
                      ))}
                    </ol>
                    {a.tryIt && (
                      <Button variant="dark" size="sm" className="mt-2.5 !h-[38px]" onClick={() => navigate(a.tryIt!.path)}>
                        {a.tryIt.label} →
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        eyebrow="Help"
        title="Tips and guides"
        actions={
          <div className="w-[260px] max-w-full">
            <Input height={40} pill value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search tips" aria-label="Search tips" leading={<Search size={15} />} className="!bg-card !text-[14px]" />
          </div>
        }
      />
      {cards.length === 0 ? (
        <EmptyState title="No tips match that" body="Try another word, or ask us from Support." />
      ) : (
        <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(min(280px,100%),1fr))' }}>
          {cards.map(({ c, articles }) => (
            <button
              key={c.id}
              type="button"
              onClick={() => {
                setOpenId(query.trim() && articles.length === 1 ? articles[0].id : null);
                setParams({ c: c.id });
              }}
              className={cx('flex flex-col gap-2 text-left rounded-[22px] border border-line bg-card p-[22px] hover:border-line2 hover:bg-card-h')}
              style={{ transition: 'border-color .2s ease, background .2s ease' }}
            >
              <span className="text-[11.5px] font-semibold px-[9px] py-1 rounded-full bg-fill2 text-t3 self-start">
                {articles.length} {articles.length === 1 ? 'tip' : 'tips'}
              </span>
              <span className="text-[17px] font-semibold tracking-[-0.01em] mt-1.5">{c.title}</span>
              <span className="text-[14px] text-t3 leading-[1.55] wp-pretty">{c.description}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
